import {
  Bell,
  Building2,
  KeyRound,
  Plug,
  ScrollText,
  Settings,
  SlidersHorizontal,
  Users,
} from 'lucide-react';

import type { Role } from '@/stores/session';
import type { Guide } from '../types';

/** Admin / Settings — Founder & Super Admin only (mirrors the nav gate). */
const ADMIN_ROLES: Role[] = ['super_admin', 'founder'];

export const ADMIN_GUIDES: Guide[] = [
  {
    slug: 'admin-overview',
    title: 'Administering the workspace',
    module: 'admin',
    summary: 'Org settings, people & roles, integrations, fields and the audit log.',
    icon: Settings,
    roles: ADMIN_ROLES,
    body: `**Admin (Settings)** is where administrators configure the workspace
itself — the controls everyone else's experience runs on.

### What's here
- **Organization** — your company identity, billing details and bank accounts.
- **Members & Roles** — who's in the workspace and what they can do.
- **Integrations** — connect external services.
- **Notification Preferences** — how alerts reach you.
- **Custom Fields** — extend records with your own fields.
- **API Keys & Bots** — server-to-server access for automations (e.g. the MCP server).
- **Audit Log** — the trail of sensitive changes.

### Who sees it
Restricted to **administrators** — these settings shape the whole tenant.`,
  },
  {
    slug: 'admin-organization',
    title: 'Organization',
    module: 'admin',
    summary: 'Company identity, billing details and receiving bank accounts.',
    icon: Building2,
    roles: ADMIN_ROLES,
    body: `**Organization** holds your workspace's identity and billing setup.

### What you can manage
- **Name, logo and brand colour** — shown across the app and on documents.
- **Invoice issuer details** — legal name, address, contact, **registration / tax ID**
  and country, printed on invoices.
- **Company bank accounts** — your receiving accounts; the **default** one is printed
  on client invoice PDFs.
- Slug and base currency are **fixed at provisioning** (read-only).

### Why it's here
These are tenant-wide settings that feed billing, PDFs and branding everywhere — so
they live in one authoritative place, changed only by admins.`,
  },
  {
    slug: 'admin-members-roles',
    title: 'Members & Roles',
    module: 'admin',
    summary: 'Invite people and control what each can see and do.',
    icon: Users,
    roles: ADMIN_ROLES,
    body: `**Members & Roles** is where you manage who belongs to the organization and
what they're allowed to do.

### How to use it
- **Invite** a person by email; they claim the account and set a password.
- Assign one or more **roles** (a member can hold several at once) — roles *are*
  the permissions.
- **Resend** an invite if it's pending.

### Roles in brief
Super Admin, Founder, Accountant, HR, Sales Manager, Lead Gen, Employee — and
**Guest** (a client, deny-by-default, who only sees their own company's portal).

### Why it's here
Access control is the backbone of a multi-team workspace. Managing members and
roles here keeps every module's permissions consistent and auditable.`,
  },
  {
    slug: 'admin-integrations',
    title: 'Integrations',
    module: 'admin',
    summary: 'Connect external services and see their status.',
    icon: Plug,
    roles: ADMIN_ROLES,
    body: `**Integrations** is where you connect the workspace to outside services and
check whether each is configured.

### How to use it
- Review the available integrations and their **connection status**.
- Connect or configure the ones you use; some features (e.g. channel analytics,
  news feeds, e-sign) light up once their integration is set up.

### Why it's here
A single place to wire the platform into your stack — and to see at a glance
what's connected and what still needs setup.`,
  },
  {
    slug: 'admin-notifications',
    title: 'Notification Preferences',
    module: 'admin',
    summary: 'Choose how alerts reach you — per channel and per kind of notification.',
    icon: Bell,
    roles: ADMIN_ROLES,
    body: `**Notification Preferences** controls how the workspace reaches you with alerts.

### What notifies you
- Someone **mentions** you in a comment, note or document.
- You're **assigned** to a work item, or an item you're assigned to or created
  **changes status** — never for your own changes.
- An **invoice is issued** to you, and the weekly digest.

### How to use it
- **Delivery channels** turn a channel (in-app pop-ups / email / Slack / Telegram) on or off
  for everything.
- **By notification type** switches one kind of notification off for one channel and keeps
  it on the others — for example, keep mention emails but mute status-change emails.
- The **header bell** always keeps every notification and updates on its own — instantly
  where live updates are set up, otherwise within a minute.

### Why it's here
Everyone wants to be reached differently. Per-channel preferences keep people
informed without drowning them — on the channel they actually watch.`,
  },
  {
    slug: 'admin-custom-fields',
    title: 'Custom Fields',
    module: 'admin',
    summary: 'Extend records with your own fields.',
    icon: SlidersHorizontal,
    roles: ADMIN_ROLES,
    body: `**Custom Fields** let you add your own data points to records, beyond the
built-in fields.

### How to use it
- Define a custom field (its name, type and which entity it belongs to).
- It then appears on that entity's records for everyone to fill in.

### Why it's here
Every agency tracks something a little different. Custom fields let you tailor the
system to how *you* work — without code or waiting on a release.`,
  },
  {
    slug: 'admin-api-keys-bots',
    title: 'API Keys & Bots',
    module: 'admin',
    summary: 'Issue keys for server-to-server access — service bots and personal tokens.',
    icon: KeyRound,
    roles: ADMIN_ROLES,
    body: `**API Keys & Bots** is where you issue keys that let outside software act on
your workspace over the API — for example the **MCP server** (which lets AI tools
read and write your Projects, Pages/Wiki, CRM and HRM), scripts, or automations
like Zapier.

Every key is **org-scoped**: it only ever acts inside this organization, and only
within the **roles** attached to it — the **same** permissions, field-masking and
isolation a normal person gets. A key is never more powerful than its roles.

### Two kinds of key
- **Service bot** — a standalone service account you create here and grant roles to.
  Use it for automations and integrations. Choose roles to fit: a *read-only* bot,
  a *Sales* bot, or a full-access *Founder* bot.
- **Personal token** — bound to an **existing person**. It acts **as them**, with
  their own roles and project/page access, and audit is credited to them. Use it for
  "act as me" access. A personal token can't do more than that person can, and
  should be treated like their password.

### How to create one
1. Click **New bot**, choose **Service bot** or **Existing user**.
2. For a bot, give it a name and tick the **roles** it needs. For a personal token,
   pick the person it acts as.
3. **Copy the key — it's shown only once.** Store it somewhere safe (a secrets
   manager). You can't see it again, only **rotate** it.

### One key per bot
Each bot is its own service account with its own key — there's no shared token
across bots. **Rotate** replaces a single bot's key (the old one stops working);
**Revoke** disables it immediately. Prefer a **separate bot per use-case** (e.g.
read-only, sales-write, admin), and a **personal token per person** when you want
individual audit and the ability to revoke just one.

### Access to private content
A key follows the **same access rules as a human**:
- An **admin** bot (Founder / Super Admin) sees and acts on **everything** in the org.
- A **narrower** bot only sees **public** projects/pages automatically; for a
  **private** project, page or wiki you must add that bot as a **member** (just like
  inviting a person), and private pages still respect their visibility.

### Managing keys
The list shows each key's name, type, prefix, roles, status and last-used time —
with search, status/type filters and table or card views. From here you **rotate**
or **revoke** any key.

### Security
- The raw key is shown **once**, on create and on rotate — never again.
- Treat keys like passwords: don't paste them in chat or commit them; share via a
  secrets manager.
- **Revoke** the moment a key leaks or a person leaves; rotate periodically.`,
  },
  {
    slug: 'admin-audit-log',
    title: 'Audit Log',
    module: 'admin',
    summary: 'The immutable trail of sensitive changes.',
    icon: ScrollText,
    roles: ADMIN_ROLES,
    body: `The **Audit Log** is the record of who changed what — focused on sensitive
actions like compensation, payroll, invoices, roles and org settings.

### How to use it
- Browse the trail to see the **actor, action, target and time** of a change.
- Use it to investigate "who did this, and when?".

### Why it's here
Trust in a system that holds money and people data depends on accountability. The
audit log makes sensitive changes traceable and tamper-evident — by design, it's
append-only.`,
  },
];
